import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShareAlt,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import alertIntelligenceAPI from "../services/AlertIntelligenceAPI";
import { formatDateOnly } from "../utils/DateTimeUtil";
import LoadingSpinner from "../common/LoadingSpinner";
import { useSubscription } from "../context/SubscriptionContext";
import "../css/Common.css";
import "./css/AlertsIntelligenceHub.css";

/**
 * Full detail view for a single alert.
 * Shows complete information, educational content, prevention tips,
 * and related training links.
 *
 * @param {number} alertId - Alert ID (for loading if alertData not provided)
 * @param {Object} alertData - Full alert data from API
 * @param {boolean} loading - Loading state
 * @param {Function} onMarkReviewed - Handler for marking reviewed
 * @param {Function} onStartTraining - Handler for starting training
 * @param {Function} onShare - Handler for sharing
 * @param {Function} onViewRelated - Handler for viewing related alert
 * @param {Function} onBack - Handler for going back
 */
function AlertDetail({
  alertId,
  alertData,
  loading,
  isMobile = false,
  onMarkReviewed,
  onStartTraining,
  onShare,
  onViewRelated,
  onBack,
}) {
  const [showFullContent, setShowFullContent] = useState({
    summary: false,
    howItWorks: false,
    redFlags: false,
    prevention: false,
  });

  const { canShare } = useSubscription();
  const hasShareCapability = canShare();

  // Ref for scrolling to top on load
  const detailRef = useRef(null);

  // Scroll to top when alert data loads
  useEffect(() => {
    if (alertData && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [alertData]);

  // ═══════════════════════════════════════════════════════════════════════════
  // LOADING STATE
  // ═══════════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="aih-detail">
        <LoadingSpinner message="Loading alert details..." />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // NOT FOUND STATE
  // ═══════════════════════════════════════════════════════════════════════════

  if (!alertData) {
    return (
      <div className="aih-detail">
        <div className="aih-detail-error">
          <span className="aih-error-icon">!</span>
          <h3>Alert Not Found</h3>
          <p>This alert may have been removed or is no longer available.</p>
          <button className="aih-back-btn" onClick={onBack}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const alert = alertData;
  const threatLabel = alertIntelligenceAPI.getThreatLevelLabel(
    alert.threatLevel,
  );
  const threatColor = alertIntelligenceAPI.getThreatLevelColor(
    alert.threatLevel,
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Toggle expanded content section
   */
  const toggleSection = (section) => {
    setShowFullContent((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  /**
   * Render a collapsible content section
   */
  const renderSection = (
    title,
    icon,
    content,
    sectionKey,
    defaultOpen = true,
  ) => {
    if (!content) return null;

    const isExpanded = showFullContent[sectionKey] || defaultOpen;
    const isLong = content.length > 500;

    return (
      <section className="aih-detail-section">
        <h3 className="aih-detail-section-title">{title}</h3>
        <div
          className={`aih-detail-section-content ${
            isExpanded ? "expanded" : "collapsed"
          }`}
        >
          {isExpanded || !isLong ? (
            <div dangerouslySetInnerHTML={{ __html: formatContent(content) }} />
          ) : (
            <>
              <div
                dangerouslySetInnerHTML={{
                  __html: formatContent(content.slice(0, 500) + "..."),
                }}
              />
              <button
                className="aih-show-more-btn"
                onClick={() => toggleSection(sectionKey)}
              >
                Read More
              </button>
            </>
          )}
        </div>
      </section>
    );
  };

  /**
   * Render red flags as a list
   */
  const renderRedFlags = () => {
    const flags = alert.recognitionSigns || [];
    if (flags.length === 0) return null;

    return (
      <section className="aih-detail-section aih-red-flags-section">
        <h3 className="aih-detail-section-title">Red Flags to Watch For</h3>
        <ul className="aih-red-flags-list">
          {flags.map((flag, index) => (
            <li key={index} className="aih-red-flag-item">
              <span className="aih-red-flag-icon">!</span>
              <span className="aih-red-flag-text">{flag}</span>
            </li>
          ))}
        </ul>
      </section>
    );
  };

  /**
   * Render prevention tips as a list
   */
  const renderPreventionTips = () => {
    const tips = alert.actionSteps || [];
    if (tips.length === 0) return null;

    return (
      <section className="aih-detail-section aih-prevention-section">
        <h3 className="aih-detail-section-title">How to Protect Yourself</h3>
        <ol className="aih-prevention-list">
          {tips.map((tip, index) => (
            <li key={index} className="aih-prevention-item">
              <span className="aih-prevention-number">{index + 1}</span>
              <span className="aih-prevention-text">{tip}</span>
            </li>
          ))}
        </ol>
      </section>
    );
  };

  /**
   * Render real examples
   */
  const renderExamples = () => {
    // Backend sends exampleText as a string, split by "Example" markers
    let examples = [];
    if (alert.exampleText) {
      examples = alert.exampleText
        .split(/Example \d+:/i)
        .filter((e) => e.trim());
    }
    if (examples.length === 0) return null;

    return (
      <section className="aih-detail-section aih-examples-section">
        <h3 className="aih-detail-section-title">Real-World Examples</h3>
        <div className="aih-examples-list">
          {examples.map((example, index) => (
            <div key={index} className="aih-example-card">
              <span className="aih-example-label">Example {index + 1}</span>
              <p className="aih-example-text">{example}</p>
            </div>
          ))}
        </div>
      </section>
    );
  };

  /**
   * Render related training link
   */
  const renderTrainingLink = () => {
    if (!alert.relatedTrainingUuid) return null;

    return (
      <section className="aih-detail-section aih-training-section">
        <h3 className="aih-detail-section-title">Related Training</h3>
        <div className="aih-training-card">
          <div className="aih-training-content">
            <p className="aih-training-description">
              Learn more about how to protect yourself from this type of scam
              with our interactive training module.
            </p>
          </div>
          <button
            className="aih-start-training-btn"
            onClick={() =>
              onStartTraining?.(alert.id, alert.relatedTrainingUuid)
            }
          >
            Start Training →
          </button>
        </div>
      </section>
    );
  };

  /**
   * Render external resources
   */
  const renderResources = () => {
    const resources = alert.externalResources || [];
    if (resources.length === 0) return null;

    return (
      <section className="aih-detail-section aih-resources-section">
        <h3 className="aih-detail-section-title">Additional Resources</h3>
        <ul className="aih-resources-list">
          {resources.map((resource, index) => (
            <li key={index} className="aih-resource-item">
              <a
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="aih-resource-link"
              >
                <span className="aih-resource-icon">
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </span>
                <span className="aih-resource-title">
                  {resource.title || resource.url}
                </span>
                {resource.source && (
                  <span className="aih-resource-source">
                    via {resource.source}
                  </span>
                )}
              </a>
            </li>
          ))}
        </ul>
      </section>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="aih-detail" ref={detailRef}>
      {/* Header */}
      <header className="aih-detail-header">
        {/* Threat level banner */}
        <div
          className="aih-detail-threat-banner"
          style={{ backgroundColor: threatColor }}
        >
          <span className="aih-threat-level-label">{threatLabel} THREAT</span>
          <span className="aih-threat-level-value">
            Level {alert.threatLevel}/10
          </span>
        </div>

        {/* Title */}
        <h1 className="aih-detail-title">{alert.title}</h1>

        {/* Meta info */}
        <div className="aih-detail-meta">
          <span className="aih-detail-category">
            {getCategoryIcon(alert.category)}{" "}
            {alert.categoryDisplayName || formatCategory(alert.category)}
          </span>
          <span className="aih-detail-separator">•</span>
          <span className="aih-detail-time">
            Published{alert.publishedDate ? ` ${alert.publishedDate}` : ""}
          </span>
          {alert.isAtPeakActivity && (
            <>
              <span className="aih-detail-separator">•</span>
              <span className="aih-detail-peak">↑ Peak Activity</span>
            </>
          )}
        </div>

        {/* Tags */}
        {alert.tags?.length > 0 && (
          <div className="aih-detail-tags">
            {alert.tags.map((tag, index) => (
              <span key={index} className="aih-detail-tag">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Affected regions */}
        {alert.geographicScope?.length > 0 && (
          <div className="aih-detail-regions">
            <span className="aih-regions-label">Affected Regions:</span>
            <span className="aih-regions-list">
              {alert.geographicScope.join(", ")}
            </span>
          </div>
        )}

        {/* User state */}
        {alert.reviewedByUser && (
          <div className="aih-detail-reviewed-banner">
            <span className="aih-reviewed-icon">✓</span>
            <span>You've reviewed this alert</span>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="aih-detail-body">
        {/* Summary */}
        {renderSection(
          "Summary",
          null,
          alert.fullDescription || alert.shortDescription,
          "summary",
          true,
        )}

        {/* How it works - render scammer tactics as formatted list */}
        {alert.scammerTactics && alert.scammerTactics.length > 0 && (
          <section className="aih-detail-section">
            <h3 className="aih-detail-section-title">How This Scam Works</h3>
            <ul className="aih-tactics-list">
              {alert.scammerTactics.map((tactic, index) => (
                <li key={index} className="aih-tactic-item">
                  {tactic}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Red flags */}
        {renderRedFlags()}

        {/* Prevention tips */}
        {renderPreventionTips()}

        {/* Real examples */}
        {renderExamples()}

        {/* Related training */}
        {renderTrainingLink()}

        {/* External resources */}
        {renderResources()}

        {/* Report statistics */}
        <section className="aih-detail-section aih-stats-section">
          <h3 className="aih-detail-section-title">Statistics</h3>
          <div className="aih-detail-stats-grid">
            <div className="aih-detail-stat">
              <span className="aih-stat-icon">•</span>
              <span className="aih-stat-value">
                {formatNumber(alert.viewCount || 0)}
              </span>
              <span className="aih-stat-label">Views</span>
            </div>
            <div className="aih-detail-stat">
              <span className="aih-stat-icon">
                <FontAwesomeIcon icon={faShareAlt} />
              </span>
              <span className="aih-stat-value">
                {formatNumber(alert.shareCount || 0)}
              </span>
              <span className="aih-stat-label">Shares</span>
            </div>
            {alert.reportedCases > 0 && (
              <div className="aih-detail-stat">
                <span className="aih-stat-icon">•</span>
                <span className="aih-stat-value">
                  {formatNumber(alert.reportedCases)}
                </span>
                <span className="aih-stat-label">Reported Cases</span>
              </div>
            )}
            {alert.estimatedLosses > 0 && (
              <div className="aih-detail-stat">
                <span className="aih-stat-icon">$</span>
                <span className="aih-stat-value">
                  ${formatNumber(alert.estimatedLosses)}
                </span>
                <span className="aih-stat-label">Est. Losses</span>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer actions */}
      <footer className="aih-detail-footer">
        {/* Action buttons */}
        <div
          className={`aih-detail-actions${isMobile ? " aih-detail-actions-mobile" : ""}`}
        >
          {!alert.reviewedByUser && (
            <button
              className={`aih-action-btn aih-review-btn${isMobile ? " aih-action-btn-mobile" : ""}`}
              onClick={() => onMarkReviewed?.(alert.id)}
            >
              <span className="aih-btn-icon">✓</span>
              Mark as Reviewed
            </button>
          )}

          <button
            className={`aih-action-btn aih-share-btn${isMobile ? " aih-action-btn-mobile" : ""}${!hasShareCapability ? " aih-action-btn-disabled" : ""}`}
            onClick={hasShareCapability ? () => onShare?.(alert) : undefined}
            disabled={!hasShareCapability}
            title={
              hasShareCapability
                ? "Share this alert with family members in your account"
                : "Available For Family or Extended Family Plans"
            }
          >
            <span className="aih-btn-icon">
              <FontAwesomeIcon icon={faShareAlt} />
            </span>
            Share with Family
          </button>

          {alert.relatedTrainingUuid && (
            <button
              className={`aih-action-btn aih-training-btn${isMobile ? " aih-action-btn-mobile" : ""}`}
              onClick={() =>
                onStartTraining?.(alert.id, alert.relatedTrainingUuid)
              }
            >
              <span className="aih-btn-icon">T</span>
              Start Training
            </button>
          )}
        </div>

        {/* Last updated */}
        {alert.updatedAt && alert.updatedAt !== alert.createdAt && (
          <div className="aih-detail-updated">
            Last updated: {formatDateOnly(alert.updatedAt, "short")}
          </div>
        )}
      </footer>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

function formatCategory(category) {
  if (!category) return "";
  return category
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
}

function getCategoryIcon(category) {
  // Return first letter of category as a simple indicator
  if (!category) return "•";
  const formatted = formatCategory(category);
  return formatted.charAt(0).toUpperCase();
}

function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
}

/**
 * Format content for safe HTML rendering
 * Handles basic markdown-like formatting
 */
function formatContent(text) {
  if (!text) return "";

  // Escape HTML first
  let formatted = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Convert line breaks
  formatted = formatted.replace(/\n\n/g, "</p><p>").replace(/\n/g, "<br>");

  // Bold text **text**
  formatted = formatted.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Italic text *text*
  formatted = formatted.replace(/\*(.*?)\*/g, "<em>$1</em>");

  return `<p>${formatted}</p>`;
}

export default AlertDetail;
